import React from 'react';

import { ChangeOrderDirection } from '../../../Api/CRM/Modules/FaqsApi';
import LotteriesApi from '../../../Api/CRM/Modules/LotteriesApi';
import Lottery from '../../../Api/Models/Lottery';
import { formatDateStringWithTimezone as formatDateStringWithTz } from '../../../utils/formatters';
import ActionButton from '../../elements/ActionButton';
import Search from '../../elements/Search';
import BooleanIcon from '../../elements/booleanIcon';
import Card from '../../elements/common/Card';
import { TableCell as Cell } from '../../elements/common/TableDetailsRow';
import TableHead from '../../elements/common/TableHead';
import TableLoader, { markModelListRow } from '../../elements/common/TableLoader';
import renderDrawRules from '../../elements/lotteries/DrawRules';
import renderNumbers from '../../elements/lotteries/Numbers';
import Paginator from '../../elements/paginator';
import { PERMISSION } from '../../helpers/Permissions';
import useAutoPaginator from '../../hooks/useAutoPaginator';

import LotteryPromoteButton from './LotteryPromoteButton';
import LotteryToggleButton from './LotteryToggleButton';

export default function LotteriesList() {
    const { items, updateItems, isLoading, auto } = useAutoPaginator<Lottery>((page, params) =>
        LotteriesApi.getAll(page, { params })
    );

    async function handleClick(event: React.MouseEvent, lottery: Lottery, direction: ChangeOrderDirection) {
        event.preventDefault();
        markModelListRow(lottery.id);
        const doNotSwap = items.some((item) => item !== lottery && item.display_order === lottery.display_order);
        const changedLottery = await LotteriesApi.changeOrder(lottery.display_slug, direction, doNotSwap);

        if (changedLottery.display_order !== lottery.display_order) {
            await updateItems();
            markModelListRow(changedLottery.id);
        }
    }

    const lotteriesList = items.map((lottery) => (
        <tr key={`${lottery.id}-${lottery.display_order}`} id={`model-list-row-${lottery.id}`}>
            <td>
                <span className="mr-2">{lottery.display_order}</span>
                <a href="#" onClick={(e) => handleClick(e, lottery, 'up')}>
                    <i className="far fa-arrow-alt-circle-up mr-1 fa-2x align-middle" />
                </a>
                {lottery.display_order > 0 && (
                    <a href="#" onClick={(e) => handleClick(e, lottery, 'down')}>
                        <i className="far fa-arrow-alt-circle-down fa-2x align-middle" />
                    </a>
                )}
            </td>
            <td>
                <ActionButton
                    url={`/lotteries/${lottery.display_slug}`}
                    title="open"
                    permissions={[PERMISSION.VIEW_LOTTERY]}
                />
                <LotteryToggleButton lottery={lottery} onUpdate={updateItems} />
                <LotteryPromoteButton lottery={lottery} onUpdate={updateItems} />
            </td>
            <td>{lottery.name}</td>
            <td className="text-center">
                <BooleanIcon value={lottery.is_enabled} />
            </td>
            <td className="text-center">
                <BooleanIcon value={lottery.is_visible_on_web} />
            </td>
            <td className="text-center">
                <BooleanIcon value={lottery.is_default} />
            </td>
            <td className="text-center">
                <BooleanIcon value={lottery.is_promoted} />
            </td>
            <td>{renderDrawRules(lottery.lottery_rule)}</td>
            <td>{lottery.timezone}</td>
            <td>
                {`${formatDateStringWithTz(lottery.last_draw_date, lottery.timezone)} (${formatDateStringWithTz(
                    lottery.next_draw_date,
                    lottery.timezone
                )})`}
            </td>
            <Cell showIf={lottery.last_numbers != null}>
                {renderNumbers(lottery.last_numbers, lottery.name, lottery.type)}
            </Cell>
        </tr>
    ));

    return (
        <Card title="All lotteries">
            <Search {...auto.search} />
            <TableLoader isLoading={isLoading}>
                <table className="table model-list">
                    <TableHead>
                        <th>Order</th>
                        <th>Action</th>
                        <th>Name</th>
                        <th>Enabled</th>
                        <th>Visible on web</th>
                        <th>Default</th>
                        <th>Promoted</th>
                        <th>Draw rules</th>
                        <th>Timezone</th>
                        <th>Last (next) draw date</th>
                        <th>Last numbers</th>
                    </TableHead>
                    <tbody>{lotteriesList}</tbody>
                </table>
                <Paginator {...auto.paginator} />
            </TableLoader>
        </Card>
    );
}
